import cookies from 'browser-cookies';
import { useWindow } from '@uds/hooks';
import { useContext, createContext, useCallback, useMemo, useState, useEffect } from 'react';
import { invariant } from '@ux/js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { WubLoader } from '@uds/penders';
import { cssTheme } from '@uds/theme-utils';
import { classNames as classNames$1 } from '@uds/typography';
import './pending.stylesheet_is3ads.css';

const AuthContext = /*#__PURE__*/createContext(undefined);
const AuthActionContext = /*#__PURE__*/createContext(undefined);
const useAuthProvider = () => {
  const state = useContext(AuthContext);
  invariant(state != null, 'Auth context was undefined, did you forget to add the auth provider?');
  return state;
};
const useAuthActionContext = () => {
  const state = useContext(AuthActionContext);
  invariant(state != null, 'AuthAction context was undefined, did you forget to add the auth provider?');
  return state;
};

const useEndImpersonation = () => {
  const {
    hostname,
    user
  } = useAuthProvider();
  const {
    location
  } = useWindow();
  const logoutUrl = '/impersonate/logout';
  const url = new URL(logoutUrl, hostname).toString();
  const endImpersonation = useCallback(() => {
    if (user != null) {
      location.assign(url);
    }
  }, [location, url, user]);
  return [endImpersonation, url];
};
var useEndImpersonation$1 = useEndImpersonation;

const clearAuth = (cookies, getCookieDomain, cookieKeys) => () => {
  const domain = getCookieDomain();
  cookieKeys.forEach(key => {
    cookies.erase(key, {
      domain
    });
  });
};
var makeClearAuth = clearAuth;

const fetchAuth$2 = (fetch, convert) => () => {
  const idp = fetch();
  if (idp == null) {
    return null;
  }
  const user = convert(idp);
  return user;
};
var makeFetchAuth = fetchAuth$2;

const fetchInfo$2 = (cookies, cookieKey) => () => {
  var _cookies$get;
  const cookie = (_cookies$get = cookies.get(cookieKey)) != null ? _cookies$get : '';
  if (!cookie) {
    throw new Error('no auth cookie');
  }
  const idp = JSON.parse(cookie);
  if (idp == null) {
    throw new Error('no auth cookie');
  }
  return idp;
};
var makeFetchInfo = fetchInfo$2;

const getCookieDomain = () => {
  try {
    return COOKIE_DOMAIN;
  } catch (e) {
    return '';
  }
};
var getCookieDomain$1 = getCookieDomain;

const logout = (clearAuth, redirect) => args => {
  clearAuth();
  redirect(args);
};
var makeLogout = logout;

const redirectToLogin = (location, getLoginPath, getPathname) => function () {
  let {
    path
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const url = getLoginPath({
    path: getPathname(path)
  });
  location.assign(url);
};
var makeRedirectToLogin = redirectToLogin;

const redirectToLogout = (location, getLogoutPath, getPathname) => function () {
  let {
    path
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const url = getLogoutPath({
    path: getPathname(path)
  });
  location.assign(url);
};
var redirectToLogout$1 = redirectToLogout;

const getAppName$2 = () => () => {
  try {
    return APP_NAME;
  } catch (e) {
    throw new Error('APP_NAME not set');
  }
};
var getAppName$3 = getAppName$2;

const getPathname = window => path => {
  return path != null ? path : `${window.location.pathname}${window.location.search}${window.location.hash}`;
};
var getPathname$1 = getPathname;

const redirectToLevelup = (location, getLevelupPath, getPathname) => _ref => {
  let {
    level,
    path
  } = _ref;
  const url = getLevelupPath({
    path: getPathname(path),
    level
  });
  location.assign(url);
};
var redirectToLevelup$1 = redirectToLevelup;

function useLogin() {
  const window = useWindow();
  const {
    getLoginPath
  } = useAuthProvider();
  const redirect = useMemo(() => {
    return makeRedirectToLogin(window.location, getLoginPath, getPathname$1(window));
  }, [window, getLoginPath]);
  return redirect;
}

function useLogout() {
  const window = useWindow();
  const {
    location
  } = window;
  const {
    cookieKeys,
    getLogoutPath
  } = useAuthProvider();
  const clearAuth = makeClearAuth(cookies, getCookieDomain$1, cookieKeys);
  const redirectToLogout = redirectToLogout$1(location, getLogoutPath, getPathname$1(window));
  const redirect = makeLogout(clearAuth, redirectToLogout);
  return redirect;
}

const useRefreshAuth = () => {
  const {
    refetchUser
  } = useAuthActionContext();
  return refetchUser;
};
var useRefreshAuth$1 = useRefreshAuth;

function useVerifyIdentity() {
  const {
    getLevelupPath
  } = useAuthProvider();
  const window = useWindow();
  const redirect = redirectToLevelup$1(window.location, getLevelupPath, getPathname$1(window));
  return redirect;
}

let Kind;
(function (Kind) {
  Kind["CUSTOMER"] = "basic";
  Kind["DELEGATE"] = "e2s";
  Kind["JOMAX"] = "jomax";
})(Kind || (Kind = {}));
let HbiLevel;
(function (HbiLevel) {
  HbiLevel["HIGH"] = "high";
  HbiLevel["MED"] = "med";
  HbiLevel["LOW"] = "low";
})(HbiLevel || (HbiLevel = {}));

const getAppName = app => {
  if (app != null && app !== '') {
    return app;
  }
  try {
    return APP_NAME;
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error('No app name provided and no APP_NAME environment variable was defined');
    }
    throw new Error('Could not determine auth app');
  }
};
var getAppName$1 = getAppName;

// TODO - Migrate to a @ux/common package as part of refactor to hooks
const getRootDomain = url => {
  var _regex$exec;
  // We must explicitly provide TLDs of brands we support with more than one dot in the url
  // such as .co.uk or .org.uk, we can not split on the first index of dots due to sub-domains
  const TLDS = ['.co.uk'];
  const foundTld = TLDS.find(tld => url.includes(tld));
  const extension = foundTld != null ? foundTld : url.slice(url.lastIndexOf('.'));
  const regex = new RegExp(`((ote.)?[A-Za-z0-9-]+)${extension}`);
  const matches = (_regex$exec = regex.exec(url)) != null ? _regex$exec : [];
  return matches[0];
};
var getRootDomain$1 = getRootDomain;

const isBasicInfo = info => {
  return (info === null || info === void 0 ? void 0 : info.auth) === 'basic' && info.typ === 'idp';
};
const isE2sInfo = info => {
  return (info === null || info === void 0 ? void 0 : info.auth) === 'e2s' && info.typ === 'idp';
};
const isJomaxInfo = info => {
  return (info === null || info === void 0 ? void 0 : info.typ) === 'jomax';
};
const isCustomer = user => {
  return (user === null || user === void 0 ? void 0 : user.kind) === Kind.CUSTOMER;
};
const isDelegate = user => {
  return (user === null || user === void 0 ? void 0 : user.kind) === Kind.DELEGATE;
};
const isJomax = user => {
  return (user === null || user === void 0 ? void 0 : user.kind) === Kind.JOMAX;
};

const getCustomer = user => {
  if (isCustomer(user)) {
    return user;
  }
  if (isDelegate(user)) {
    return user.customer;
  }
  return null;
};
var getCustomer$1 = getCustomer;

const getDelegate = user => {
  if (isDelegate(user)) {
    return user;
  }
  return null;
};
var getDelegate$1 = getDelegate;

const getJomax = user => {
  if (isJomax(user)) {
    return user;
  }
  return null;
};
var getJomax$1 = getJomax;

const getKind = user => {
  return user === null || user === void 0 ? void 0 : user.kind;
};
var getKind$1 = getKind;

const isAuthenticated = user => {
  return user != null;
};
var isAuthenticated$1 = isAuthenticated;

const useAuth = () => {
  const {
    user
  } = useAuthProvider();
  return user;
};
var useAuth$1 = useAuth;

const useIsAuthenticated = () => {
  return isAuthenticated$1(useAuth$1());
};
var useIsAuthenticated$1 = useIsAuthenticated;

const store = {
  redirect: () => null,
  infoIdp: null
};

/**
 * We have different types of redirect, one for Jomax apps, and one for Idp apps.
 * Each type of auth provider writes to this store, and the authDriver uses this store
 * to do the redirect in case of 401. This is essentially the same as jpex did before.
 * We register a function in a global store, and later access it.
 * There is no way for the authDriver to access values in any context since it lives outside
 * of the react app, and its consumers does as well.
 */
const setRedirectFunction = fn => {
  store.redirect = fn;
};

/**
 * A user might log in with two different users in two different tabs. We need to keep
 * a copy of the initial info idp so that we can log user out if he has data related to a different account.
 */
const setInfoIdp = infoIdp => {
  store.infoIdp = infoIdp;
};

const authDriver = function () {
  let redirectStore = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : store;
  let cookieFetcher = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : cookies;
  return http => function () {
    const currentIdp = cookieFetcher.get('info_idp');
    if (redirectStore.infoIdp !== currentIdp) {
      // If a user has logged in with two different users in two different tabs, he might end up sending
      // a request on behalf of the wrong user. This is to prevent such a case.
      redirectStore.redirect();
      return Promise.reject({
        status: 401,
        error: 'Impersonation issue!'
      });
    }
    return http(...arguments).catch(e => {
      if ((e === null || e === void 0 ? void 0 : e.status) === 401) {
        redirectStore.redirect();
      }
      return Promise.reject(e);
    });
  };
};
var authDriver$1 = authDriver;

/* eslint-disable camelcase */
const basicToCustomer = idp => {
  const {
    info_cid,
    info_shopperId,
    username
  } = idp;
  return {
    kind: Kind.CUSTOMER,
    customerId: info_cid,
    shopperId: info_shopperId,
    username
  };
};
const e2sToDelegate = idp => {
  const {
    del: {
      info_accountName,
      firstname,
      lastname
    },
    e2s
  } = idp;
  return {
    kind: Kind.DELEGATE,
    username: info_accountName,
    firstName: firstname,
    lastName: lastname,
    customer: basicToCustomer(e2s)
  };
};
const convertIdpToUser$2 = idp => {
  if (isBasicInfo(idp)) {
    return basicToCustomer(idp);
  }
  if (isE2sInfo(idp)) {
    return e2sToDelegate(idp);
  }
  throw new Error('Unknown auth type');
};
var convertIdpToUser$3 = convertIdpToUser$2;

const getLevelupPath$1 = (getAppName, getHostname) => args => {
  const realm = 'idp';
  const host = getHostname();
  const app = encodeURIComponent(getAppName());
  const path = encodeURIComponent(args.path);
  const level = encodeURIComponent(args.level);
  return `${host}/identity-verification?app=${app}&path=${path}&realm=${realm}&hbi=${level}`;
};
var getLevelupPath$2 = getLevelupPath$1;

const getLoginPath$1 = (getAppName, getHostname) => args => {
  const realm = 'idp';
  const host = getHostname();
  const app = encodeURIComponent(getAppName());
  const path = encodeURIComponent(args.path);
  return `${host}?app=${app}&path=${path}&realm=${realm}`;
};
var getLoginPath$2 = getLoginPath$1;

const getLogoutPath$2 = (getAppName, getHostname) => args => {
  const realm = 'idp';
  const host = getHostname();
  const app = encodeURIComponent(getAppName());
  const path = encodeURIComponent(args.path);
  return new URL(`/logout?app=${app}&path=${path}&realm=${realm}`, host).toString();
};
var getLogoutPath$3 = getLogoutPath$2;

const getHostname$2 = location => () => {
  const root = getRootDomain$1(location.hostname);
  const host = `https://sso.${root}`;
  return host;
};
var makeGetHostname = getHostname$2;

const cookieKeys$1 = ['info_idp', 'auth_idp'];
const fetchInfo$1 = makeFetchInfo(cookies, cookieKeys$1[0]);
const fetchAuth$1 = makeFetchAuth(fetchInfo$1, convertIdpToUser$3);
const getUser$1 = () => {
  try {
    return fetchAuth$1();
  } catch (e) {
    return null;
  }
};
const IdpProvider = _ref => {
  let {
    children
  } = _ref;
  const [user, setUser] = useState(getUser$1());
  const window = useWindow();
  const {
    location
  } = window;
  const authState = useMemo(() => {
    const getHostname = makeGetHostname(location);
    const appName = getAppName$3();
    return {
      user,
      hostname: getHostname(),
      getLoginPath: getLoginPath$2(appName, getHostname),
      getLogoutPath: getLogoutPath$3(appName, getHostname),
      cookieKeys: cookieKeys$1,
      fetchUser: fetchAuth$1,
      getLevelupPath: getLevelupPath$2(appName, getHostname)
    };
  }, [user, location]);
  useEffect(() => {
    var _cookies$get;
    setInfoIdp((_cookies$get = cookies.get(cookieKeys$1[0])) != null ? _cookies$get : '');
  }, []);
  useEffect(() => {
    setRedirectFunction(redirectToLogout$1(window.location, authState.getLogoutPath, getPathname$1(window)));
  }, [window, authState.getLogoutPath]);
  const actionState = useMemo(() => {
    return {
      refetchUser: () => setUser(getUser$1())
    };
  }, []);
  return /*#__PURE__*/jsx(AuthContext.Provider, {
    value: authState,
    children: /*#__PURE__*/jsx(AuthActionContext.Provider, {
      value: actionState,
      children: children
    })
  });
};
IdpProvider.displayName = 'IdpProvider';
var IdpProvider$1 = IdpProvider;

const convertIdpToUser = idp => {
  if (!isJomaxInfo(idp)) {
    throw new Error('Unknown auth type');
  }
  return {
    kind: Kind.JOMAX,
    username: idp.info_accountName,
    firstName: idp.firstname,
    lastName: idp.lastname
  };
};
var convertIdpToUser$1 = convertIdpToUser;

var getLevelupPath = (() => () => {
  throw new Error('level up is not available for jomax logins');
});

const getLoginPath = (getAppName, getHostname) => args => {
  const realm = 'jomax';
  const host = getHostname();
  const app = encodeURIComponent(getAppName());
  const path = encodeURIComponent(args.path);
  return `${host}?realm=${realm}&app=${app}&path=${path}`;
};
var makeGetLoginPath = getLoginPath;

const getLogoutPath = getLoginPath => getLoginPath;
var getLogoutPath$1 = getLogoutPath;

const getHostname = () => {
  try {
    return SSO_REDIRECT_PATHNAME;
  } catch (e) {
    return 'https://sso.godaddy.com';
  }
};
var getHostname$1 = getHostname;

const cookieKeys = ['info_jomax', 'auth_idp'];
const fetchInfo = makeFetchInfo(cookies, cookieKeys[0]);
const fetchAuth = makeFetchAuth(fetchInfo, convertIdpToUser$1);
const getUser = () => {
  try {
    return fetchAuth();
  } catch {
    return null;
  }
};
const JomaxProvider = _ref => {
  let {
    children
  } = _ref;
  const [user, setUser] = useState(getUser());
  const window = useWindow();
  const authState = useMemo(() => {
    const appName = getAppName$3();
    const getLoginPath = makeGetLoginPath(appName, getHostname$1);
    return {
      user,
      hostname: getHostname$1(),
      getLoginPath,
      getLogoutPath: getLogoutPath$1(getLoginPath),
      cookieKeys,
      fetchUser: fetchAuth,
      getLevelupPath: getLevelupPath()
    };
  }, [user]);
  useEffect(() => {
    var _cookies$get;
    setInfoIdp((_cookies$get = cookies.get(cookieKeys[0])) != null ? _cookies$get : '');
  }, []);
  useEffect(() => {
    setRedirectFunction(redirectToLogout$1(window.location, authState.getLogoutPath, getPathname$1(window)));
  }, [window, authState.getLogoutPath]);
  const actionState = useMemo(() => {
    return {
      refetchUser: () => setUser(getUser())
    };
  }, []);
  return /*#__PURE__*/jsx(AuthContext.Provider, {
    value: authState,
    children: /*#__PURE__*/jsx(AuthActionContext.Provider, {
      value: actionState,
      children: children
    })
  });
};
JomaxProvider.displayName = 'IdpProvider';
var JomaxProvider$1 = JomaxProvider;

const root = "r1bplpnv";
const loader = "l17tt6p0";
const classNames = {
  root,
  loader,
  message: classNames$1.content
};

const Pending = _ref => {
  let {
    message = 'Pending...'
  } = _ref;
  return /*#__PURE__*/jsxs("div", {
    className: classNames.root,
    children: [/*#__PURE__*/jsx("div", {
      className: classNames.loader,
      children: /*#__PURE__*/jsx(WubLoader, {
        color: cssTheme.palette.primary,
        gu: 5
      })
    }), /*#__PURE__*/jsx("div", {
      className: classNames.message,
      children: message
    })]
  });
};
var Pending$1 = Pending;

// @ts-ignore: This is ok for testing helpers
const noop = () => null;
const defaultInitialState = {
  user: null,
  hostname: '',
  getLoginPath: noop,
  getLogoutPath: () => 'http://localhost',
  getLevelupPath: noop,
  fetchUser: () => noop,
  cookieKeys: []
};
const TestProvider = _ref => {
  var _initialState$user;
  let {
    children,
    initialState = defaultInitialState,
    user: initialUser = null
  } = _ref;
  const [user, setUser] = useState((_initialState$user = initialState.user) != null ? _initialState$user : initialUser);
  const {
    location
  } = useWindow();
  const context = useMemo(() => {
    return {
      ...initialState,
      hostname: initialState.hostname || makeGetHostname(location)(),
      user
    };
  }, [user, initialState, location]);
  const actionContext = useMemo(() => {
    return {
      refetchUser: () => setUser(initialState.user)
    };
  }, [initialState.user]);
  return /*#__PURE__*/jsx(AuthContext.Provider, {
    value: context,
    children: /*#__PURE__*/jsx(AuthActionContext.Provider, {
      value: actionContext,
      children: children
    })
  });
};
var TestProvider$1 = TestProvider;

const useAuthGuard = (useAuth, useLogin) => {
  const user = useAuth();
  const login = useLogin();
  const authenticated = isAuthenticated$1(user);
  if (!authenticated) {
    login({});
  }
};
function useUseAuthGuard() {
  return useAuthGuard(useAuth$1, useLogin);
}

export { HbiLevel, IdpProvider$1 as IdpProvider, JomaxProvider$1 as JomaxProvider, Kind, Pending$1 as Pending, TestProvider$1 as TestProvider, authDriver$1 as authDriver, getAppName$1 as getAppName, getCustomer$1 as getCustomer, getDelegate$1 as getDelegate, getJomax$1 as getJomax, getKind$1 as getKind, getRootDomain$1 as getRootDomain, isAuthenticated$1 as isAuthenticated, isBasicInfo, isCustomer, isDelegate, isE2sInfo, isJomax, isJomaxInfo, useAuth$1 as useAuth, useUseAuthGuard as useAuthGuard, useEndImpersonation$1 as useEndImpersonation, useIsAuthenticated$1 as useIsAuthenticated, useLogin, useLogout, useRefreshAuth$1 as useRefreshAuth, useVerifyIdentity };
